import { get } from 'svelte/store';
import { user_data, user_settings } from 'src/stores';
import { convertCost } from 'src/helpers/utilities';
import { convertToValueAndUnit } from 'src/helpers/units_helpers';
import { getTariffByID } from 'src/helpers/power_prices_helpers';
import { getDateByTimeZone } from 'src/helpers/date_helper';
import type { PowerPricesData } from 'src/types/PowerPrices';
import type { ChartDataInterface } from 'src/types/Chart';
import type {
	ChartDataInterface as DsChartDataInterface,
	ChartStatisticRangePeriod,
} from '@Shelly/shelly-design-system';
import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';

const TARIFF_COLORS = [
	'--color-chart-money-value',
	'--color-chart-consumed-energy',
	'--color-orange',
	'--color-purple',
	'--color-lightblue',
];

export type TariffBucket = {
	id: string;
	name: string;
	valueWh: number;
	cost: number;
	/** True when the statistics API explicitly supplied at least one cost value. */
	costReported?: boolean;
	price: number;
	color: string;
};

export type TariffRow = {
	id: string;
	name: string;
	consumptionLabel: string;
	costLabel: string | null;
	percent: number;
	color: string;
};

export type TariffSummary = {
	/** Raw, currency-formatted total cost (kept for KPI compatibility). */
	totalCost: string | null;
	totalConsumptionLabel: string;
	offPeakPercent: number;
	offPeakLabel: string;
	peakPercent: number;
	peakLabel: string;
	rows: TariffRow[];
	hasTariffs: boolean;
	hasDistinctRates: boolean;
};

export type CurrentTariffDetails = {
	name: string;
	priceLabel: string;
	tone: 'offPeak' | 'standard' | 'peak';
} | null;

const readPowerPrices = (): PowerPricesData | null => {
	const raw = get(user_settings)?.power_prices;
	if (!raw) return null;
	try {
		return JSON.parse(raw) as PowerPricesData;
	} catch {
		return null;
	}
};

const formatTariffRate = (price: number | string, currency: string): string => {
	const currencyLabel =
		({ EUR: '€', USD: '$', GBP: '£', JPY: '¥' } as Record<string, string>)[currency.trim()] ??
		currency.trim();
	if (!currencyLabel) return `${price}/kWh`;
	const separator = /^[€$£¥]/.test(currencyLabel) ? '' : ' ';
	return `${currencyLabel}${separator}${price}/kWh`;
};

export const tariffTone = (
	price: number,
	powerPrices: PowerPricesData | null,
): 'offPeak' | 'standard' | 'peak' => {
	const rates = Object.values(powerPrices?.tariffs ?? {})
		.map((tariff) => Number(tariff.price))
		.filter((rate) => rate > 0);
	if (!rates.length || price <= 0) return 'standard';
	if (new Set(rates).size < 2) return 'standard';
	if (price === Math.max(...rates)) return 'peak';
	if (price === Math.min(...rates)) return 'offPeak';
	return 'standard';
};

export const getCurrentTariffDetails = (currency: string): CurrentTariffDetails => {
	const settings = get(user_settings);
	const powerPrices = readPowerPrices();
	const liveTariff = settings?.power_prices_live_tariff;
	if (liveTariff) {
		const price = Number(liveTariff.price ?? 0);
		return {
			name: liveTariff.name || 'Live tariff',
			priceLabel: formatTariffRate(liveTariff.price, currency),
			tone: tariffTone(price, powerPrices),
		};
	}

	if (!powerPrices?.schedule?.length) return null;

	const date = getDateByTimeZone(get(user_data)?.timezone);
	const daySchedule = powerPrices.schedule[date.getDay()] ?? powerPrices.schedule[0];
	const tariffId = String(
		daySchedule?.[date.getHours() * 2 + (date.getMinutes() >= 30 ? 1 : 0)] ?? '',
	);
	const tariff = powerPrices.tariffs?.[tariffId];
	if (!tariff) return null;

	return {
		name: tariff.name || `Tariff ${tariffId}`,
		priceLabel:
			typeof tariff.price === 'number'
				? formatTariffRate(tariff.price, currency)
				: 'Price unavailable',
		tone: tariffTone(Number(tariff.price ?? 0), powerPrices),
	};
};

/**
 * Build per-tariff buckets straight from the raw overall-consumption history.
 * Each history entry carries a single `tariff_id`, `consumption` (Wh) and `cost`,
 * which is far more reliable than digging through parsed chart metadata.
 */
export const aggregateTariffBuckets = (
	history: GetStatsResponseHistoryData[] | null | undefined,
): TariffBucket[] => {
	const powerPrices = readPowerPrices();
	const buckets: Record<string, TariffBucket> = {};
	const resolveTariffId = (entry: GetStatsResponseHistoryData): string => {
		const reportedId = String(entry.tariff_id ?? '');
		return reportedId || '0';
	};

	(history ?? []).forEach((entry) => {
		if (!entry || entry.missing) return;
		const consumption = Number(entry.consumption ?? 0);
		const cost = Number(entry.cost ?? 0);
		if (consumption <= 0 && cost <= 0) return;

		const id = resolveTariffId(entry);
		if (!buckets[id]) {
			const configured = powerPrices?.tariffs?.[id];
			const fallbackName = id === '0' ? 'Untariffed' : `Tariff ${id}`;
			buckets[id] = {
				id,
				name: configured?.name || getTariffByID(id)?.name || fallbackName,
				valueWh: 0,
				cost: 0,
				costReported: false,
				price: Number(configured?.price ?? 0),
				color: TARIFF_COLORS[Object.keys(buckets).length % TARIFF_COLORS.length],
			};
		}
		buckets[id].valueWh += consumption;
		buckets[id].cost += cost;
		buckets[id].costReported ||= entry.cost != null;
	});

	return Object.values(buckets)
		.filter((b) => b.valueWh > 0 || b.cost > 0)
		.sort((a, b) => b.valueWh - a.valueWh);
};

export const buildTariffSummary = (
	buckets: TariffBucket[],
	currencyOverride?: string,
): TariffSummary => {
	const currency = currencyOverride ?? get(user_settings)?.currency ?? '';
	const powerPrices = readPowerPrices();
	const totalWh = buckets.reduce((sum, b) => sum + b.valueWh, 0);
	const totalCostRaw = buckets.reduce((sum, b) => sum + b.cost, 0);
	const hasReportedCost = buckets.some((bucket) => bucket.costReported ?? bucket.cost > 0);

	const totalDisplay = convertToValueAndUnit(totalWh, 'Wh');
	const totalConsumptionLabel = totalWh > 0 ? `${totalDisplay.value} ${totalDisplay.unit}` : '—';

	if (!buckets.length || totalWh <= 0) {
		return {
			totalCost: hasReportedCost ? convertCost(totalCostRaw, currency) : null,
			totalConsumptionLabel,
			offPeakPercent: 0,
			offPeakLabel: '—',
			peakPercent: 0,
			peakLabel: '—',
			rows: [],
			hasTariffs: false,
			hasDistinctRates: false,
		};
	}

	// Tariff classification uses the configured rate tied to the API-reported
	// tariff_id. We deliberately do not infer a rate from consumption and cost.
	const withRate = buckets.map((b) => ({
		bucket: b,
		rate: b.price > 0 ? b.price : 0,
	}));
	const configuredRates = Object.values(powerPrices?.tariffs ?? {})
		.map((tariff) => Number(tariff.price))
		.filter((rate) => rate > 0);
	const positiveRates = [...configuredRates, ...withRate.map((w) => w.rate)].filter((r) => r > 0);
	const distinctRates = new Set(positiveRates);
	// Off-peak share is only meaningful when tariffs actually differ in price.
	const hasDistinctRates = distinctRates.size > 1;
	const minRate = hasDistinctRates ? Math.min(...positiveRates) : 0;
	const offPeakWh = minRate
		? withRate.filter((w) => w.rate === minRate).reduce((sum, w) => sum + w.bucket.valueWh, 0)
		: 0;
	const offPeakPercent =
		hasDistinctRates && totalWh > 0 ? Math.min(Math.round((offPeakWh / totalWh) * 100), 100) : 0;
	const maxRate = hasDistinctRates ? Math.max(...positiveRates) : 0;
	const peakWh = maxRate
		? withRate.filter((w) => w.rate === maxRate).reduce((sum, w) => sum + w.bucket.valueWh, 0)
		: 0;
	const peakPercent =
		hasDistinctRates && totalWh > 0 ? Math.min(Math.round((peakWh / totalWh) * 100), 100) : 0;

	const rows: TariffRow[] = [...withRate]
		.sort((a, b) => a.rate - b.rate)
		.map(({ bucket: b, rate }) => {
			const display = convertToValueAndUnit(b.valueWh, 'Wh');
			return {
				id: b.id,
				name: b.name,
				consumptionLabel: `${display.value} ${display.unit}`,
				costLabel: (b.costReported ?? b.cost > 0) ? convertCost(b.cost, currency) : null,
				percent: totalWh > 0 ? Math.round((b.valueWh / totalWh) * 100) : 0,
				color:
					hasDistinctRates && rate === maxRate
						? '--color-danger'
						: hasDistinctRates && rate === minRate
							? '--color-primary'
							: b.color,
			};
		});

	return {
		totalCost: hasReportedCost ? convertCost(totalCostRaw, currency) : null,
		totalConsumptionLabel,
		offPeakPercent,
		offPeakLabel: hasDistinctRates ? `${offPeakPercent}%` : '—',
		peakPercent,
		peakLabel: hasDistinctRates ? `${peakPercent}%` : '—',
		rows,
		hasTariffs: true,
		hasDistinctRates,
	};
};

/**
 * Build a dual-axis "consumption (bar) + cost (line)" config for the tariff
 * pressure widget, reusing the already-parsed overall chart so the x-axis
 * buckets/labels stay consistent with the rest of the dashboard.
 */
export const buildTariffPressureChart = (
	chartData: ChartDataInterface | null,
	history: GetStatsResponseHistoryData[] = [],
	buckets: TariffBucket[] = [],
): DsChartDataInterface | null => {
	if (!chartData?.series?.length) return null;

	const consumption =
		chartData.series.find((s) => s.name === 'consumption' && !s.prevPeriod && s.data?.length) ??
		chartData.series.find((s) => !s.prevPeriod && s.data?.length);
	if (!consumption?.data?.length) return null;

	const points = consumption.data.filter((p) => p.dt != null);
	const consumptionData: ChartDataInterface['series'][number]['data'] = points.map((p) => ({
		dt: p.dt,
		value: Math.max(Number(p.value ?? 0), 0) / 1000,
	}));
	const costData: ChartDataInterface['series'][number]['data'] = points.map((p) => ({
		dt: p.dt,
		value: Math.max(Number(p.cost ?? 0), 0),
	}));

	if (!consumptionData.some((p) => p.value > 0) && !costData.some((p) => p.value > 0)) {
		return null;
	}

	const tariffSeries = buckets
		.map((bucket) => {
			const data = history
				.filter((entry) => !entry.missing && String(entry.tariff_id ?? '0') === bucket.id)
				.map((entry) => ({
					dt: new Date(entry.datetime.replace(' ', 'T')).getTime(),
					value: Math.max(Number(entry.consumption ?? 0), 0) / 1000,
				}))
				.filter((point) => Number.isFinite(point.dt));
			return {
				name: `tariff-${bucket.id}`,
				style: 'consumption',
				visible: true,
				data,
			};
		})
		.filter((series) => series.data.length);
	const resolvedConsumptionSeries = tariffSeries.length
		? tariffSeries
		: [
				{
					name: 'consumption',
					style: 'consumption',
					visible: true,
					data: consumptionData,
				},
			];

	return {
		id: 'tariffPressure',
		name: 'tariffPressure',
		type: 'bar',
		units: 'kWh',
		height: 180,
		rangePeriod: chartData.rangePeriod as ChartStatisticRangePeriod,
		rangeInterval: 'hour',
		timezone: chartData.timezone,
		seriesLabels: {
			consumption: 'Consumption',
			cost: 'Cost',
		},
		series: [
			...resolvedConsumptionSeries,
			{ name: 'cost', style: 'cost', visible: true, data: costData },
		],
	} as DsChartDataInterface;
};

export const getPeakCountdownLabel = (): string | null => {
	const powerPrices = readPowerPrices();
	if (!powerPrices?.tariffs || !powerPrices.schedule?.length) return null;
	const now = getDateByTimeZone(get(user_data)?.timezone);
	const schedule = powerPrices.schedule[now.getDay()] ?? powerPrices.schedule[0];
	if (!schedule?.length) return null;
	const prices = Object.values(powerPrices.tariffs)
		.map((tariff) => Number(tariff.price ?? 0))
		.filter((price) => price > 0);
	if (new Set(prices).size < 2) return null;
	const peakPrice = Math.max(...prices);
	if (peakPrice <= 0) return null;

	const currentSlot = now.getHours() * 2 + (now.getMinutes() >= 30 ? 1 : 0);
	const currentPrice = Number(powerPrices.tariffs[String(schedule[currentSlot])]?.price ?? 0);
	if (currentPrice >= peakPrice) return 'Peak now';

	for (let index = currentSlot + 1; index < schedule.length; index++) {
		const price = Number(powerPrices.tariffs[String(schedule[index])]?.price ?? 0);
		if (price < peakPrice) continue;
		const minutesUntil = Math.max(index * 30 - (now.getHours() * 60 + now.getMinutes()), 0);
		const hours = Math.floor(minutesUntil / 60);
		const minutes = minutesUntil % 60;
		return hours > 0
			? `Peak in ${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`
			: `Peak in ${minutes}m`;
	}

	return null;
};
